import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { ArrowsClockwise, Package, TShirt, Tree, Horse, Trophy, Target } from '@phosphor-icons/react';
import api from '../../utils/api';

/**
 * AddonsTab — show producers a roll-up of every add-on riders bought
 * (show shirts with sizes, shavings bags, tie-out fees) and a per-rider
 * breakdown so the office knows what to pull from inventory.
 */
export const AddonsTab = ({ showId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get(`/shows/${showId}/addons-summary`);
      setData(payload);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load add-ons');
    } finally {
      setLoading(false);
    }
  }, [showId]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="py-8 text-center text-[#9E9E9E] text-sm">Loading…</div>;
  }
  if (!data) return null;

  const { items, summary } = data;
  const tshirtSizes = Object.entries(summary.tshirt.by_size || {});

  return (
    <div data-testid="addons-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <StatCard
          icon={TShirt}
          label={summary.tshirt.label}
          qty={summary.tshirt.total_qty}
          revenue={summary.tshirt.total_revenue}
          color="#0096FF"
          testid="addons-stat-tshirt"
        />
        <StatCard
          icon={Horse}
          label={summary.tie_out.label}
          qty={summary.tie_out.total_qty}
          revenue={summary.tie_out.total_revenue}
          color="#FF9500"
          testid="addons-stat-tieout"
        />
        <StatCard
          icon={Tree}
          label={summary.shavings.label}
          qty={summary.shavings.total_qty}
          revenue={summary.shavings.total_revenue}
          color="#34C759"
          testid="addons-stat-shavings"
        />
        <StatCard
          icon={Trophy}
          label={summary.sidepots?.label || 'Sidepots'}
          qty={summary.sidepots?.total_qty || 0}
          revenue={summary.sidepots?.total_revenue || 0}
          color="#AF52DE"
          testid="addons-stat-sidepots"
        />
        <StatCard
          icon={Target}
          label={summary.target_race?.label || 'Target Race'}
          qty={summary.target_race?.total_qty || 0}
          revenue={summary.target_race?.total_revenue || 0}
          color="#FF2D55"
          testid="addons-stat-target"
        />
        <StatCard
          icon={Package}
          label="Grand Total"
          qty={summary.rider_count}
          qtyLabel="riders"
          revenue={summary.grand_total_revenue}
          color="#FF3B30"
          testid="addons-stat-total"
        />
      </div>

      {/* T-Shirt size breakdown (only when shirts were sold) */}
      {tshirtSizes.length > 0 && (
        <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg p-4 mb-5" data-testid="addons-tshirt-sizes">
          <div className="flex items-center gap-2 mb-3">
            <TShirt size={14} className="text-[#0096FF]" weight="duotone" />
            <span className="text-[10px] uppercase tracking-wider text-[#9E9E9E]">
              {summary.tshirt.label} — by Size
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {tshirtSizes.map(([size, qty]) => (
              <span
                key={size}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0F1F2A] border border-[#0096FF]/30 rounded text-xs"
                data-testid={`addons-tshirt-size-${size}`}
              >
                <span className="font-bold text-white">{size}</span>
                <span className="text-[#0096FF] font-mono">× {qty}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Refresh + Title row */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-[#9E9E9E]">
          {items.length} rider{items.length === 1 ? '' : 's'} purchased add-ons
        </div>
        <button
          onClick={load}
          className="px-2.5 py-1.5 bg-[#0A0A0A] border border-[#262626] hover:border-[#FF9500] text-[#9E9E9E] hover:text-white rounded transition-colors"
          title="Refresh"
          data-testid="addons-refresh-btn"
        >
          <ArrowsClockwise size={12} />
        </button>
      </div>

      {/* Per-rider breakdown */}
      {items.length === 0 ? (
        <div className="py-12 text-center bg-[#0A0A0A] border border-dashed border-[#262626] rounded-lg" data-testid="addons-empty">
          <Package size={32} className="text-[#262626] mx-auto mb-2" />
          <p className="text-sm text-[#9E9E9E]">No add-ons purchased yet.</p>
          <p className="text-xs text-[#555] mt-1">Riders can add show shirts, tie-out, and shavings during entry.</p>
        </div>
      ) : (
        <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded overflow-x-auto" data-testid="addons-table">
          <table className="w-full text-xs">
            <thead className="bg-[#161616] text-left">
              <tr className="text-[10px] uppercase text-[#9E9E9E]">
                <th className="px-3 py-2 font-semibold">Rider / Horse</th>
                <th className="px-3 py-2 font-semibold">{summary.tshirt.label}</th>
                <th className="px-3 py-2 font-semibold">{summary.tie_out.label}</th>
                <th className="px-3 py-2 font-semibold">{summary.shavings.label}</th>
                <th className="px-3 py-2 font-semibold">Sidepots</th>
                <th className="px-3 py-2 font-semibold">Target Race</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.entry_id} className="border-t border-[#1F1F1F]" data-testid={`addons-row-${row.entry_id}`}>
                  <td className="px-3 py-2">
                    <div className="text-white font-semibold whitespace-nowrap">{row.rider_name}</div>
                    <div className="text-[#9E9E9E] text-[10px]">{row.horse_name || '—'}</div>
                  </td>
                  <td className="px-3 py-2 text-white whitespace-nowrap">
                    {row.tshirt ? (() => {
                      const items = Array.isArray(row.tshirt.items) ? row.tshirt.items : null;
                      const label = items && items.length > 0
                        ? items.map((it) => `${it.qty}×${it.size || '—'}`).join(', ')
                        : `${row.tshirt.qty} × ${row.tshirt.size || '—'}`;
                      return <span>{label} <span className="text-[#9E9E9E]">@ ${Number(row.tshirt.unit_price || 0).toFixed(2)}</span></span>;
                    })() : <span className="text-[#555]">—</span>}
                  </td>
                  <td className="px-3 py-2 text-white whitespace-nowrap">
                    {row.tie_out ? (
                      row.tie_out.qty
                        ? <span>{row.tie_out.qty}{row.tie_out.days > 1 ? ` × ${row.tie_out.days}d` : ''} <span className="text-[#9E9E9E]">@ ${Number(row.tie_out.unit_price || 0).toFixed(2)}</span></span>
                        : <span>${Number(row.tie_out.total || row.tie_out.price || 0).toFixed(2)}</span>
                    ) : <span className="text-[#555]">—</span>}
                  </td>
                  <td className="px-3 py-2 text-white whitespace-nowrap">
                    {row.shavings ? (
                      <span>{row.shavings.qty} bag{row.shavings.qty === 1 ? '' : 's'} <span className="text-[#9E9E9E]">@ ${Number(row.shavings.unit_price || 0).toFixed(2)}</span></span>
                    ) : <span className="text-[#555]">—</span>}
                  </td>
                  <td className="px-3 py-2 text-white">
                    {row.sidepots && row.sidepots.length > 0 ? (
                      <div className="flex flex-col gap-0.5">
                        {row.sidepots.map((sp, i) => (
                          <span key={i} className="text-[11px] whitespace-nowrap">
                            <span className="text-white">{sp.name}</span>
                            <span className="text-[#9E9E9E]"> · {sp.class_name} @ </span>
                            <span className="text-[#AF52DE] font-mono">${Number(sp.fee || 0).toFixed(0)}</span>
                          </span>
                        ))}
                      </div>
                    ) : <span className="text-[#555]">—</span>}
                  </td>
                  <td className="px-3 py-2 text-white">
                    {row.target_race && (Array.isArray(row.target_race) ? row.target_race.length > 0 : row.target_race) ? (
                      <div className="flex flex-col gap-0.5">
                        {(Array.isArray(row.target_race) ? row.target_race : [row.target_race]).map((tp, i) => (
                          <span key={i} className="text-[11px] whitespace-nowrap">
                            <span className="text-white">{tp.name || 'Target Race'}</span>
                            {tp.class_name ? <span className="text-[#9E9E9E]"> · {tp.class_name}</span> : null}
                            <span className="text-[#9E9E9E]"> @ </span>
                            <span className="text-[#FF2D55] font-mono">${Number(tp.fee || 0).toFixed(0)}</span>
                            {tp.payment_status && tp.payment_status !== 'PAID' ? (
                              <span className="ml-1 text-[9px] uppercase font-bold text-[#FF9500]">·{tp.payment_status}</span>
                            ) : null}
                          </span>
                        ))}
                      </div>
                    ) : <span className="text-[#555]">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <PaymentBadge status={row.payment_status} />
                  </td>
                  <td className="px-3 py-2 text-right text-white font-bold">${Number(row.row_total || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[#161616]">
              <tr className="border-t border-[#1F1F1F]">
                <td colSpan={7} className="px-3 py-2.5 text-right text-[#9E9E9E] uppercase text-[10px] font-bold tracking-wider">Grand Total</td>
                <td className="px-3 py-2.5 text-right text-[#FF9500] font-black text-base" data-testid="addons-grand-total">
                  ${Number(summary.grand_total_revenue || 0).toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, qty, qtyLabel = '', revenue, color, testid }) => (
  <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded p-3" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <p className="text-[9px] uppercase tracking-wider text-[#9E9E9E] truncate">{label}</p>
      <Icon size={14} style={{ color }} weight="duotone" />
    </div>
    <p className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit' }}>
      {qty || 0}
      {qtyLabel && <span className="text-xs text-[#9E9E9E] ml-1 font-normal">{qtyLabel}</span>}
    </p>
    <p className="text-[10px] text-[#9E9E9E] mt-1">
      <span style={{ color }}>${Number(revenue || 0).toFixed(2)}</span> revenue
    </p>
  </div>
);

const PaymentBadge = ({ status }) => {
  const styles = {
    PAID: { bg: 'bg-[#34C759]/15', fg: 'text-[#34C759]' },
    PENDING: { bg: 'bg-[#FACC15]/15', fg: 'text-[#FACC15]' },
    UNPAID: { bg: 'bg-[#FF3B30]/15', fg: 'text-[#FF3B30]' },
    COMP: { bg: 'bg-[#0096FF]/15', fg: 'text-[#0096FF]' },
    REFUNDED: { bg: 'bg-[#AF52DE]/15', fg: 'text-[#AF52DE]' },
  }[status] || { bg: 'bg-[#9E9E9E]/15', fg: 'text-[#9E9E9E]' };
  return (
    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${styles.bg} ${styles.fg}`}>
      {status || 'UNPAID'}
    </span>
  );
};
